/**
 * Unified translation resolver — the single source of truth for how a key
 * becomes a string, in EVERY translation call site across the app.
 *
 * Resolution order (highest priority first):
 *   1. core        — coreTranslations (demo/chart/nav/compliance chrome)
 *   2. portal      — portalTranslations (Katcon, Nexteer; EN/ES only, EN fallback)
 *   3. demo        — TREMEC demoTranslations (EN/ES only, EN fallback)
 *   4. general     — buildTranslations() catch-all dictionary
 *   5. fallback    — caller-supplied fallback string
 *   6. key         — the raw key, as a last resort
 *
 * WHY THIS EXISTS:
 * The app previously had two parallel lookups that silently diverged —
 * useGlobalTranslation walked core → portal → demo → general, while
 * useTranslation resolved ONLY the general dictionary. Any component on the
 * lightweight hook missed core/portal/demo entirely, which is how English
 * strings leaked into the Spanish (MX/demo) experience. Both hooks now delegate
 * here, so they can never diverge again.
 *
 * LEAF MODULE: imports only leaf dictionaries. It must NOT import a React hook
 * or a translation hook — doing so reintroduces the import cycle this fix broke.
 */
import type { Language } from '@/contexts/SettingsContext';
import { coreTranslations } from '@/i18n/coreTranslations';
import { portalTranslations } from '@/i18n/portalTranslations';
import { translations as demoTranslations } from '@/demo/tremec/i18n/translations';
import { getTranslationsForLanguage } from '@/i18n/generalDictionary';

/**
 * Resolve a translation key for a given language, walking the full dictionary
 * chain. This is the one function both useTranslation and useGlobalTranslation
 * delegate to.
 */
export function resolveTranslation(
  key: string,
  language: Language | string,
  fallback?: string,
): string {
  // 1. Core app translations (highest priority)
  const coreDict = coreTranslations[language as Language];
  if (coreDict && coreDict[key] !== undefined) {
    return coreDict[key];
  }

  // Portal + demo dictionaries are EN/ES only. Spanish falls back to English.
  const enEsLang: 'en' | 'es' = language === 'es' ? 'es' : 'en';

  // 2. Portal translations (Katcon, Nexteer)
  const portalDict = portalTranslations[enEsLang];
  if (portalDict && key in portalDict) {
    return portalDict[key];
  }
  if (enEsLang === 'es') {
    const portalEnDict = portalTranslations.en;
    if (portalEnDict && key in portalEnDict) return portalEnDict[key];
  }

  // 3. Demo translations (TREMEC)
  const demoDict = demoTranslations[enEsLang];
  if (demoDict && key in demoDict) {
    return demoDict[key];
  }
  if (enEsLang === 'es') {
    const demoEnDict = demoTranslations.en;
    if (demoEnDict && key in demoEnDict) return demoEnDict[key];
  }

  // 4. General translation system (catch-all dictionary).
  //    getTranslationsForLanguage already falls back to EN for the whole dict;
  //    we only accept a hit if the key actually exists, so an unknown key falls
  //    through to the caller fallback / raw key rather than returning EN noise.
  const generalDict = getTranslationsForLanguage(language);
  if (generalDict && generalDict[key] !== undefined) {
    return generalDict[key];
  }

  // 5. Caller-supplied fallback, then 6. the raw key.
  return fallback ?? key;
}
